import React, { useRef, useState, useEffect } from "react";
import * as posenet from "@tensorflow-models/posenet";
import * as tf from "@tensorflow/tfjs"; // Import TensorFlow
import "@tensorflow/tfjs";

const RomAllowCamera = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [counter, setCounter] = useState(0);
  const [romMessage, setRomMessage] = useState("No detection found.");
  const [detectedAngle, setDetectedAngle] = useState(null);
  const [selectedLeg, setSelectedLeg] = useState(null);

  useEffect(() => {
    const loadPosenet = async () => {
      await tf.ready();
      const net = await posenet.load();
      const video = await setupCamera();
      
      if (video) {
        video.play();
        detectPoseInRealTime(video, net);
      }
    };
    

    const setupCamera = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Browser API navigator.mediaDevices.getUserMedia not available");
      }

      const video = videoRef.current;

      if (!video) {
        return null;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        video.srcObject = stream;

        return new Promise((resolve) => {
          video.onloadedmetadata = () => {
            resolve(video);
          };
        });
      } catch (error) {
        console.error("Error accessing camera: ", error);
        return null;
      }
    };

    const detectPoseInRealTime = (video, net) => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = 960;
      canvas.height = 720;

      const poseDetectionFrame = async () => {
        const pose = await net.estimateSinglePose(video, {
          flipHorizontal: false,
        });

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        drawKeypoints(pose.keypoints, 0.5, ctx);
        drawSkeleton(pose.keypoints, 0.5, ctx);

        if (selectedLeg === "Right") {
          detectLeg(pose, ctx, "right");
        } else if (selectedLeg === "Left") {
          detectLeg(pose, ctx, "left");
        } else if (selectedLeg === "Both") {
          detectLeg(pose, ctx, "right");
          detectLeg(pose, ctx, "left");
        }

        requestAnimationFrame(poseDetectionFrame);
      };
      poseDetectionFrame();
    };

    loadPosenet();
  }, [selectedLeg]);

  const drawKeypoints = (keypoints, minConfidence, ctx) => {
    keypoints.forEach((keypoint) => {
      if (keypoint.score > minConfidence) {
        const { y, x } = keypoint.position;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, 2 * Math.PI);
        ctx.fillStyle = "#15B1D7";
        ctx.fill();
      }
    });
  };

  const drawSkeleton = (keypoints, minConfidence, ctx) => {
    const adjacentKeyPoints = posenet.getAdjacentKeyPoints(keypoints, minConfidence);

    adjacentKeyPoints.forEach((keypoints) => {
      const { x: x1, y: y1 } = keypoints[0].position;
      const { x: x2, y: y2 } = keypoints[1].position;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#15B1D7";
      ctx.stroke();
    });
  };

  const detectLeg = (pose, ctx, side) => {
    const keypoints = pose.keypoints;
    const ankle = keypoints.find((point) => point.part === `${side}Ankle`);
    const knee = keypoints.find((point) => point.part === `${side}Knee`);
    const hip = keypoints.find((point) => point.part === `${side}Hip`);

    if (ankle && ankle.score > 0.5) {
      if (knee && hip) {
        const angle = calculateAngle(hip.position, knee.position, ankle.position);

        if (angle >= 0) {
          setCounter((prevCounter) => prevCounter + 1);
          setDetectedAngle(Math.round(angle));
          updateRomMessage(angle);

          // Draw angle line on the canvas
          drawAngleLine(ctx, hip.position, knee.position, ankle.position, angle);
        }
      }
    } else {
      setRomMessage("No detection found.");
      setDetectedAngle(null); // Reset the angle when no detection
    }
  };

  const calculateAngle = (hip, knee, ankle) => {
    const radians =
      Math.atan2(ankle.y - knee.y, ankle.x - knee.x) -
      Math.atan2(hip.y - knee.y, hip.x - knee.x);
    let angle = Math.abs((radians * 180) / Math.PI);
    if (angle > 180) angle = 360 - angle;
    return angle;
  };

  const updateRomMessage = (angle) => {
    if (angle < 83) {
      setRomMessage("You're getting there! Keep working towards 83° to climb stairs safely.");
    } else if (angle >= 83 && angle <= 89) {
      setRomMessage("Awesome! You can climb stairs now. A little more to reach a ROM safe for descending stairs!");
    } else if (angle === 90) {
      setRomMessage("Great job! You've reached 90°, safe to descend stairs with ease.");
    } else if (angle >= 91 && angle <= 104) {
      setRomMessage("You're almost there! Keep going, and you'll soon be standing up from a chair with no problem.");
    } else if (angle === 105) {
      setRomMessage("Amazing! At 105°, standing up from a chair just got easier!");
    } else if (angle >= 106 && angle <= 114) {
      setRomMessage("You're getting close to biking time! Just a bit more until 115°.");
    } else if (angle === 115) {
      setRomMessage("Fantastic! You’ve hit 115°, ready to ride a bike!");
    } else if (angle >= 116 && angle <= 134) {
      setRomMessage("You're doing great! Keep pushing, full flexion is right around the corner.");
    } else if (angle === 135) {
      setRomMessage("Full flexion achieved! Your knee is at its maximum range of motion.");
    }
  };

  const drawAngleLine = (ctx, hip, knee, ankle, angle) => {
    ctx.beginPath();
    ctx.moveTo(hip.x, hip.y);
    ctx.lineTo(knee.x, knee.y);
    ctx.lineTo(ankle.x, ankle.y);
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#FC6448";
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(knee.x, knee.y, 7, 0, 2 * Math.PI);
    ctx.fillStyle = "#FC6448";
    ctx.fill();

    ctx.font = "16px Arial";
    ctx.fillStyle = "white";
    ctx.fillText(`${Math.round(angle)}°`, knee.x + 10, knee.y - 10);
  };

  return (
    <div className="divContainerCheck">
      {!selectedLeg ? (
        <div>
          <p>Select which leg to track:</p>
          <div className="buttonSelector">
          <button onClick={() => setSelectedLeg("Left")}>Left</button>
          <button onClick={() => setSelectedLeg("Right")}>Right</button>
          {/* <button onClick={() => setSelectedLeg("Both")}>Both</button> */}
          </div>
        </div>
      ) : (
        <>
          <video ref={videoRef} style={{ display: "none" }} width="960" height="720" autoPlay />
          <canvas ref={canvasRef} width="960" height="720" style={{ border: "1px solid black" }} />
          <p>Counter ({selectedLeg}): {counter} | Angle: {detectedAngle}°</p>
          <p>{romMessage}</p>
        </>
      )}
    </div>
  );
};

export default RomAllowCamera;
